import { useEffect, useState } from "react";
import styles from "./collection-list-page.module.css";
import { getLastPathComponentFromCurrentURL } from "PosterEditor/utils/url-parsing";
import { useCollectionStore } from "entity-store/collections-store/use-collection-store";
import { collectionIDsForHandle } from "./collection-list-map";
import { TemplateCollection } from "PosterEditor/model/template-collection";
import { useTemplateStore } from "entity-store/template-store/use-template-store";
import { ProjectTemplate } from "PosterEditor/model/project-template";
import { ProjectPreview } from "components/common/project-preview";
import { PageLoadingIndicator } from "admin/shared-components/page-loading-indicator";
import {
  Breadcrumbs,
  BreadcrumbItem,
} from "collection-pages/bread-crumbs/breadcrumbs";
import { usePaths } from "BEDROCK/paths";
import { CTAButton } from "BEDROCK/shared-components/cta-button";
import { useCreateProjectWithStrava } from "strava-acquisition/hooks/use-create-project-with-strava";

interface CollectionListPageProps {}

export const CollectionListPage = (props: CollectionListPageProps) => {
  const { home } = usePaths();
  const [collectionHandle, setCollectionHandle] = useState<string | null>(null);
  const { loadCollectionsByIDs } = useCollectionStore();
  const [collections, setCollections] = useState<TemplateCollection[]>([]);

  useEffect(() => {
    const handle = getLastPathComponentFromCurrentURL();
    if (handle) {
      const collectionIDs = collectionIDsForHandle(handle);
      loadCollectionsByIDs(collectionIDs).then((collections) => {
        setCollections(collections);
      });
    }

    setCollectionHandle(handle);
  }, []);

  const breadcrumbs: BreadcrumbItem[] = [
    {
      iconName: "HOME",
      path: home,
    },
    {
      label: "All styles",
    },
  ];

  if (collections.length === 0) {
    return <PageLoadingIndicator />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.header}>
          <Breadcrumbs items={breadcrumbs} />
        </div>
        <div className={styles.collectionList}>
          {collections.map((collection) => (
            <CollectionSection collection={collection} key={collection.id} />
          ))}
        </div>
      </div>
    </div>
  );
};

interface CollectionSectionProps {
  collection: TemplateCollection;
}
const CollectionSection = (props: CollectionSectionProps) => {
  const { navigateTo, collection } = usePaths();
  const { loadTemplatesByIDs } = useTemplateStore();
  const { createProjectWithTemplate } = useCreateProjectWithStrava();
  const [templates, setTemplates] = useState<ProjectTemplate[]>([]);

  useEffect(() => {
    //Only take up to the first 6 template ids
    const templateIDs = props.collection.templateIDs.slice(0, 9);
    loadTemplatesByIDs(templateIDs).then((templates) => {
      setTemplates(templates);
    });
  }, [props.collection.templateIDs]);

  const seeAll = () => {
    navigateTo(collection + "/" + props.collection.id);
  };

  return (
    <div className={styles.collectionSection}>
      <div className={styles.header}>
        <span className={styles.title}>{props.collection.name}</span>
        <CTAButton
          title="See all"
          onClick={seeAll}
          variant="LINK"
          iconPosition="RIGHT"
          iconName="CHEVRON-RIGHT"
        />
      </div>
      <div className={styles.body}>
        {templates.map((template) => (
          <button
            className={styles.template}
            onClick={() => {
              createProjectWithTemplate(template);
            }}
          >
            <ProjectPreview
              template={template}
              size={{ width: 300, height: 300 }}
              showOutline={true}
              allowHoverEffect={true}
              fallbackToMapImage={false}
            />
          </button>
        ))}
        {templates.length < 6 &&
          Array.from({ length: 6 - templates.length }).map((_, idx) => (
            <div className={styles.placeholder} key={`placeholder-${idx}`}>
              <div
                key={`placeholder-${idx}`}
                className={styles.placeholderContent}
              />
            </div>
          ))}
      </div>
    </div>
  );
};
